import React, { useState, useRef, useEffect } from 'react';
import { Container, Typography, Button, TextField, Box, Autocomplete, Chip } from '@mui/material';
import Grid from '@mui/material/Grid2';
import { toast } from 'react-toastify';
import KeyboardBackspaceIcon from '@mui/icons-material/KeyboardBackspace';
import { useNavigate } from 'react-router-dom';
import type { FormData } from '../../../../../types';
import ProductBasicInfo from '../../../../../components/page/products/ProductBasicInfo';
import ProductDescription from '../../../../../components/page/products/ProductDescription';
import ProductCategory from '../../../../../components/page/products/ProductCategory';
// import { addProduct, updateProduct } from '../../../../../services/api/ProductAPI';
import ProductOptions from '../../../../../components/page/products/ProductOptions';
import WholesaleFields from '../../../../../components/page/products/WholesaleFields';
import BulkDiscountFields from '../../../../../components/page/products/BulkDiscountFields';
import ProductImages from '../../../../../components/page/products/ProductImages';
import ColorVariants from '../../../../../components/page/products/ColorVariants';
import Footer from './Footer';
// import zIndex from '@mui/material/styles/zIndex';

const initialFormData: FormData = {
	name: '',
	category: '',
	description: '',
	price: '',
	quantity: '',
	bestSelling: false,
	trending: false,
	productImage: [],
	weight: '',
	weightUnit: 'kg',
	isInquiry: false,
	isBulkDiscount: false,
	isCustomized: false,
	slug: '',
	isWholesale: false,
	wholesaleFields: [],
	buldDiscountsData: [],
	wholesaleMinQuantity: 1,
	isStickoutPrice: false,
	isColorVariantAvailable: false,
	strickoutPrice: 1,
	colorVariants: [],
	imageAltTags: [],
};

const ViewProduct = ({
	product,
	editenabel,
	handleSubmit,
	setModalAction,
	setEditEnabel,
	setModalOpen,
	setVendorApprove,
}: any) => {
	console.log(product, 'product>>>');
	const [formData, setFormData] = useState<FormData>(initialFormData);

	useEffect(() => {
		setFormData({
			name: product?.name ?? '',
			category: product?.category ?? '',
			description: product?.description ?? '',
			price: product?.price ?? '',
			quantity: product?.quantity ?? '',
			bestSelling: product?.bestSelling ?? false,
			trending: product?.trending ?? false,
			productImage: product?.image ?? [],
			weight: product?.weight ?? '',
			weightUnit: product?.weightUnit ?? 'kg',
			isInquiry: product?.isInquiry ?? false,
			isBulkDiscount: product?.isBulkDiscount ?? false,
			isCustomized: product?.isCustomized ?? false,
			slug: product?.slug ?? '',
			isWholesale: product?.isWholesale ?? false,
			wholesaleFields: product?.wholesaleFields ?? [],
			buldDiscountsData: product?.buldDiscountsData ?? [],
			wholesaleMinQuantity: product?.wholesaleMinQuantity ?? 1,
			isStickoutPrice: product?.isStickoutPrice ?? false,
			isColorVariantAvailable: product?.isColorVariantAvailable ?? false,
			strickoutPrice: product?.strickoutPrice ?? 1,
			colorVariants: product?.colorVariants ?? [],
			imageAltTags: product?.imageAltTags ?? [],
		});
		setSeo({
			metaTitle: product?.metaTitle || '',
			metaDescription: product?.metaDescription || '',
			canonicalTag: product?.canonicalTag || '',
			imageAltTag: product?.imageAltTag || '',
			keywords: product?.keywords || [],
		});
	}, [product]);

	const [loading, setLoading] = useState(false);
	const [seo, setSeo] = useState<any>({
		metaTitle: product?.metaTitle || '',
		metaDescription: product?.metaDescription || '',
		canonicalTag: product?.canonicalTag || '',
		imageAltTag: product?.imageAltTag || '',
		keywords: product?.keywords || [],
	});

	const quillRef = useRef<any>(null);
	const navigate = useNavigate();
	const slug = localStorage.getItem('slug');
	const location = window.location.pathname.split('/');

	// Prefill Quill editor when product.description exists
	useEffect(() => {
		if (product?.description && quillRef.current) {
			quillRef.current.getEditor().clipboard.dangerouslyPasteHTML(product.description);
		}
	}, [product]);

	const handlerSubmit = async (e: React.FormEvent) => {
		e.preventDefault();

		if (!formData.isColorVariantAvailable && formData.productImage.length === 0) {
			toast.error('Please upload at least one image.');
			return;
		}

		const formDataToSend = new FormData();

		// Basic product info
		formDataToSend.append('name', formData.name);
		formDataToSend.append('slug', formData.slug);
		formDataToSend.append('description', formData.description);
		formDataToSend.append('category', formData.category);
		formDataToSend.append('trending', String(formData.trending));
		formDataToSend.append('metaTitle', seo.metaTitle);
		formDataToSend.append('metaDescription', seo.metaDescription);
		formDataToSend.append('canonicalTag', seo.canonicalTag);

		// Product images
		formData.productImage.forEach((file) => {
			formDataToSend.append('productImage', file);
		});
		seo.keywords.forEach((text: any, index: any) => {
			formDataToSend.append(`keywords[${index}]`, text);
		});

		formData.imageAltTags.forEach((tag: any) => {
			formDataToSend.append('imageAltTag', tag);
		});

		// Product options
		if (!formData.isInquiry) {
			formDataToSend.append('weight', formData.weight);
			if (!formData.isColorVariantAvailable) {
				formDataToSend.append('price', formData.price.toString());
			}
			formDataToSend.append('quantity', formData.quantity);
			formDataToSend.append('bestSelling', String(formData.bestSelling));
			if (formData.isWholesale) {
				formDataToSend.append(
					'wholesaleMinQuantity',
					String(formData.wholesaleMinQuantity)
				);
			}
			formDataToSend.append('weightUnit', formData.weightUnit);
			formDataToSend.append('isBulkDiscount', String(formData.isBulkDiscount));
			formDataToSend.append('isCustomized', String(formData.isCustomized));
			formDataToSend.append('isWholesale', String(formData.isWholesale));
		}

		formDataToSend.append('isInquiry', String(formData.isInquiry));
		formDataToSend.append('isColorVariantAvailable', String(formData.isColorVariantAvailable));
		formDataToSend.append('isStickoutPrice', String(formData.isStickoutPrice));
		if (formData.isStickoutPrice) {
			formDataToSend.append('strickoutPrice', String(formData.strickoutPrice));
		}

		// Wholesale fields
		formData.wholesaleFields.forEach((field, index) => {
			formDataToSend.append(`wholesaleFields[${index}][type]`, field.type);
			formDataToSend.append(`wholesaleFields[${index}][label]`, field.label);
			formDataToSend.append(`wholesaleFields[${index}][required]`, String(field.required));
		});

		// Bulk discounts
		formData.buldDiscountsData.forEach((discount, index) => {
			formDataToSend.append(
				`bulkDiscounts[${index}][minQuantity]`,
				String(discount.minQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][maxQuantity]`,
				String(discount.maxQuantity)
			);
			formDataToSend.append(
				`bulkDiscounts[${index}][discountPercentage]`,
				String(discount.discountPercentage)
			);
		});

		// Color variants
		formData.colorVariants.forEach((variant, variantIndex) => {
			formDataToSend.append(`colorVariants[${variantIndex}][colorName]`, variant.colorName);
			formDataToSend.append(
				`colorVariants[${variantIndex}][colorCode]`,
				variant.colorCode || '#c62020'
			);
			formDataToSend.append(`colorVariants[${variantIndex}][price]`, String(variant.price));
			formDataToSend.append(
				`colorVariants[${variantIndex}][strickoutPrice]`,
				String(variant.strickoutPrice)
			);

			variant.sizes.forEach((sizeObj, sizeIndex) => {
				formDataToSend.append(
					`colorVariants[${variantIndex}][sizes][${sizeIndex}][size]`,
					sizeObj.size
				);
			});
			variant.imageAltTag.forEach((img: any) => {
				formDataToSend.append(`colorVariants[${variantIndex}][imageAltTag]`, img);
			});

			variant.colorImage.forEach((img, imgIndex) => {
				if (img.file) {
					formDataToSend.append(`colorVariants[${variantIndex}][colorImage]`, img.file);
				} else if (img.url) {
					formDataToSend.append(
						`colorVariants[${variantIndex}][colorImage][${imgIndex}]`,
						img.url
					);
				}
			});
		});

		try {
			setLoading(true);
			// const response = await updateProduct(formDataToSend, product._id);
			const response = await handleSubmit(formDataToSend, product._id);
			if (response.isError) {
				if (response.data.errors) {
					toast.error(response.data.errors);
				} else if (response.data.message) {
					toast.error(response.data.message);
				}
			} else {
				toast.success(response.message || 'Product added successfully!');
				resetForm();
				navigate(
					location.includes(slug as string) ? `/${slug}/product` : '/vendor/product'
				);
			}
		} catch (error) {
			console.error('Error:', error);
			toast.error('Failed to save product.');
		} finally {
			setLoading(false);
		}
	};

	const resetForm = () => {
		setFormData(initialFormData);
		if (quillRef.current) {
			quillRef.current.getEditor().setContents([]);
		}
	};

	return (
		<div>
			<Grid container alignItems="center" justifyContent="space-between" sx={{ mb: 2 }}>
				<Typography
					gutterBottom
					style={{ fontFamily: 'Poppins', fontWeight: '700' }}
					sx={{ fontSize: { xs: '14px', sm: '18px', md: '20px' } }}
				>
					{editenabel ? 'View Product' : 'Edit Product'}
				</Typography>
				<Button
					variant="outlined"
					color="error"
					onClick={() => window.history.back()}
					startIcon={<KeyboardBackspaceIcon />}
					disabled={loading}
					// sx={{ fontSize: { xs: 14, sm: 18 } }}
				>
					Go Back
				</Button>
			</Grid>
			<Container>
				<form onSubmit={handlerSubmit}>
					<Grid container spacing={2} sx={{ pt: 2 }}>
						<ProductBasicInfo
							formData={formData}
							setFormData={setFormData}
							disabled={editenabel}
						/>

						<ProductDescription
							formData={formData}
							setFormData={setFormData}
							quillRef={quillRef}
							disabled={editenabel}
						/>

						<ProductCategory
							formData={formData}
							setFormData={setFormData}
							disabled={editenabel}
						/>

						<ProductOptions
							formData={formData}
							setFormData={setFormData}
							disabled={editenabel}
						/>

						{!formData.isInquiry && (
							<>
								{formData.isWholesale && (
									<WholesaleFields
										formData={formData}
										setFormData={setFormData}
										disabled={editenabel}
									/>
								)}

								{formData.isBulkDiscount && (
									<BulkDiscountFields
										formData={formData}
										setFormData={setFormData}
										disabled={editenabel}
									/>
								)}
							</>
						)}

						{!formData.isColorVariantAvailable ? (
							<ProductImages
								formData={formData}
								setFormData={setFormData}
								disabled={editenabel}
							/>
						) : (
							<ColorVariants
								formData={formData}
								setFormData={setFormData}
								disabled={editenabel}
							/>
						)}
					</Grid>
					<Box sx={{ pt: 2 }}>
						<Box sx={{ fontWeight: 'bold', mb: 2, fontSize: { xs: 14, sm: 18 } }}>
							SEO
						</Box>
						<Grid container spacing={2}>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="MetaTitle"
										fullWidth
										disabled={editenabel}
										value={seo.metaTitle}
										onChange={(e) =>
											setSeo({ ...seo, metaTitle: e.target.value })
										}
										InputProps={{
											sx: { fontSize: 14, sm: 18 }, // 👈 label size
										}}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="Meta Description"
										disabled={editenabel}
										fullWidth
										value={seo.metaDescription}
										onChange={(e) =>
											setSeo({ ...seo, metaDescription: e.target.value })
										}
										InputProps={{
											sx: { fontSize: 14, sm: 18 }, // 👈 label size
										}}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<TextField
										size="small"
										label="Canonical Tag"
										disabled={editenabel}
										fullWidth
										value={seo.canonicalTag}
										onChange={(e) =>
											setSeo({ ...seo, canonicalTag: e.target.value })
										}
										InputProps={{
											sx: { fontSize: 14, sm: 18 }, // 👈 label size
										}}
									/>
								</Box>
							</Grid>
							<Grid size={{ xs: 12, sm: 6 }}>
								<Box>
									<Autocomplete
										multiple
										freeSolo
										disabled={editenabel}
										options={[]}
										value={seo.keywords || []}
										onChange={(_, newValue) =>
											setSeo({
												...seo,
												keywords: newValue
													.map((v) => v.trim())
													.filter((v) => v !== ''),
											})
										}
										renderTags={(value, getTagProps) =>
											value.map((option, index) => (
												<Box key={index}>
													<Chip
														variant="outlined"
														label={option}
														{...getTagProps({ index })}
														sx={{ fontFamily: 'Poppins' }}
													/>
												</Box>
											))
										}
										renderInput={(params) => (
											<TextField
												{...params}
												variant="outlined"
												size="small"
												label="Keywords"
												placeholder="Type and press Enter"
												disabled={editenabel}
											/>
										)}
									/>
								</Box>
							</Grid>
						</Grid>
					</Box>
					<Grid container sx={{ mt: 3 }}>
						<Grid size={{ xs: 12 }} display="flex" justifyContent="flex-end" gap={2}>
							<Footer
								setModalOpen={setModalOpen}
								setModalAction={setModalAction}
								setEditEnabel={setEditEnabel}
								editenabel={editenabel}
								loading={loading}
								product={product}
								setVendorApprove={setVendorApprove}
							/>
						</Grid>
					</Grid>
				</form>
			</Container>
		</div>
	);
};

export default ViewProduct;
